import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { GridColumn } from '@progress/kendo-react-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import {
  ContextMenu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import { plusIcon } from "@progress/kendo-svg-icons";

import { useKeyPress } from '../../../../hooks';

import { Button, ListingGrid } from '../../../common';
import DepartmentDialog from './DepartmentDialog';

import { departmentsService } from '../../../../services/payroll/departmentsService';

import { DepartmentListType } from '../../../../data/types';
import { columns } from './model';

import { ButtonWrapper, ListingGridWrapper } from '../PersonnelCodes.styled';

type SelectionType = { field?: string; dataItem: any };

interface DepartmentsProps {
  selectedDepartment: DepartmentListType | undefined;
  setSelectedDepartment: React.Dispatch<
    React.SetStateAction<DepartmentListType | undefined>
  >;
}

const Departments: FC<DepartmentsProps> = ({
  selectedDepartment,
  setSelectedDepartment,
}) => {
  const [originData, setOriginData] = useState<DepartmentListType[]>([]);
  const [filterData, setFilterData] = useState<DepartmentListType[]>([]);
  const [dataTemplate, setDataTemplate] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<SortDescriptor[]>();
  const [showContext, setShowContext] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SelectionType>();

  const offset = useRef({ left: 0, top: 0 });

  async function fetchData() {
    try {
      const departmentList: any = await departmentsService.getDepartments();
      setOriginData(departmentList ?? []);
      setFilterData(departmentList ?? []);

      const departmentData: any = await departmentsService.getDepartmentData()
      setDataTemplate(departmentData)

    } catch (e) { }

    setIsLoading(false);
  }

  useEffect(() => {
    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContextMenuOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    offset.current = {
      left: e.pageX,
      top: e.pageY,
    };
    setShowContext(true);
  };

  const handleOnSelect = useCallback(
    (e: MenuSelectEvent) => {
      if (selectedItem) {
        const { field, dataItem } = selectedItem;
        switch (e.item.data.action) {
          case 'sortAsc':
            setSort([
              {
                dir: 'asc',
                field: field ?? '',
              },
            ]);
            break;
          case 'sortDesc':
            setSort([
              {
                dir: 'desc',
                field: field ?? '',
              },
            ]);
            break;
          case 'filterIn':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] === dataItem[field]
                )
              );
            }
            break;
          case 'filterOut':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] !== dataItem[field]
                )
              );
            }
            break;
          case 'removeFilters':
            setFilterData(originData);
            break;
          default:
        }
      }
      setShowContext(false);
    },
    [selectedItem, originData]
  );

useKeyPress(
    () => {
    },
    () => {
    },
    () => {
    },
    () => {
      setSelectedDepartment({ DepartmentCode: '' });
    }
  );

  return (
    <ListingGridWrapper>
      {isLoading ? (
        <></>
      ) : (
        <>
          <ListingGrid
            rowHeight={24}
            data={sort ? orderBy(filterData, sort) : filterData}
            dataItemKey="DepartmentCode"
            onRowClick={(e) => {
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.DepartmentCode === e.dataItem.DepartmentCode,
                }))
              );
            }}
            onRowDoubleClick={(e) => {
              setSelectedDepartment(e.dataItem);
            }}
            sortable={true}
            sort={sort}
            onSortChange={(e) => {
              setSort(e.sort);
            }}
            onContextMenu={(event) => {
              handleContextMenuOpen(event.syntheticEvent);
              const { dataItem, field } = event;
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.DepartmentCode === dataItem.DepartmentCode,
                }))
              );
              setSelectedItem({ field, dataItem });
            }}
          >
            {columns.map((column, index) => {
              return <GridColumn {...column} key={index} />;
            })}
          </ListingGrid>
          <ButtonWrapper>
            <Button
              onClick={(e) => {
                e.preventDefault();
                setSelectedDepartment({ DepartmentCode: '' });
              }}
              id="create-btn"
              title="Add New"
              svgIcon={plusIcon}
            >
              ADD <u>N</u>EW
            </Button>
          </ButtonWrapper>
        </>
      )}
      <ContextMenu
        show={showContext}
        offset={offset.current}
        onSelect={handleOnSelect}
        onClose={() => setShowContext(false)}
        className="context-menu"
      >
        <MenuItem
          text="Sort Ascending"
          data={{
            action: 'sortAsc',
          }}
        />
        <MenuItem
          text="Sort Descending"
          data={{
            action: 'sortDesc',
          }}
        />
        <MenuItem
          text="Filter In Selected"
          data={{
            action: 'filterIn',
          }}
        />
        <MenuItem
          text="Filter Out Selected"
          data={{
            action: 'filterOut',
          }}
        />
        <MenuItem
          text="Remove All Filters"
          data={{
            action: 'removeFilters',
          }}
        />
      </ContextMenu>
      {selectedDepartment && (
        <DepartmentDialog
          handleClose={(shouldRefreshData?: boolean) => {
            setSelectedDepartment(undefined);
            if (shouldRefreshData) {
              fetchData();
            }
          }}
          data={selectedDepartment}
          isEdit={selectedDepartment?.DepartmentCode ? true : false}
          dataTemplate={dataTemplate}
        />
      )}
    </ListingGridWrapper>
  );
};

export default Departments;
